/**
 * Pure helpers for per-agent model specs (#1490).
 *
 * Shared by the two admin model-picker surfaces — the global
 * `Agents → Assignments` page and the per-plan editor's Agent models
 * section — so the parse/compose/price-status logic lives in exactly
 * one place and can't drift between the two. All pure functions, no
 * React, no I/O.
 */
import type { AdminAgentName, AgentModelBar, AgentModelOverride, ModelPricingRow, ModelSupplierStatus, ModelUnavailableReason } from './api'

/** Compose a `<provider>:<model>` namePattern from an AgentModelOverride
 * (the doc shape) — falls back to bare model when provider is unset.
 * Returns `''` when neither is set so a combined dropdown collapses to
 * the "cascade / inherit" sentinel. */
export function overrideToNamePattern(o: AgentModelOverride | undefined): string {
  if (!o?.model) return ''
  return o.provider ? `${o.provider}:${o.model}` : o.model
}

/** Split a namePattern on its FIRST colon — `openai:gpt-5.4-mini` →
 * `{ provider: 'openai', model: 'gpt-5.4-mini' }`. Model ids
 * occasionally contain `:` themselves; splitting on the first colon
 * preserves the rest as the model id. A pattern with no colon is
 * treated as model-only (provider undefined) — the resolver tolerates
 * that and a few pricing-catalog rows use this shape. */
export function parseNamePattern(pattern: string): { provider?: string; model: string } {
  const i = pattern.indexOf(':')
  if (i < 0) return { model: pattern }
  return {
    provider: pattern.slice(0, i),
    model: pattern.slice(i + 1),
  }
}

/** Why a model cannot be picked on this install, or null when it can.
 *
 * Read from the server's answer, never worked out here: the catalog row says
 * `available: false` for a model it knows, and the supplier list covers a
 * saved model the catalog has no row for. An empty pattern is the
 * "inherit" sentinel and is never unavailable. */
export function modelUnavailableReason(
  namePattern: string,
  rowByPattern: ReadonlyMap<string, ModelPricingRow>,
  suppliers: readonly ModelSupplierStatus[],
  /** The server's answer for the agent's resolved model, which covers a
   * saved model the catalog has retired and so has no row for. */
  resolved?: { spec: string; unavailableReason?: ModelUnavailableReason },
): ModelUnavailableReason | null {
  if (!namePattern) return null
  if (resolved && resolved.spec === namePattern && resolved.unavailableReason) return resolved.unavailableReason
  const row = rowByPattern.get(namePattern)
  if (row?.available === false) return row.unavailableReason ?? 'notListed'
  if (row) return null
  const { provider } = parseNamePattern(namePattern)
  const state = suppliers.find(s => s.supplier === provider)?.state
  if (state === 'noKey' || state === 'keyRejected') return state
  return null
}

/** The plain reason a model is unavailable, naming its supplier. Worded as
 * the server's save refusal is, so the two read the same. */
export function unavailableReasonText(
  reason: ModelUnavailableReason,
  supplierName: string,
): string {
  if (reason === 'unsupported') return `OttoTester can't call ${supplierName} models directly.`
  if (reason === 'noPrice') return "OttoTester has no price for this model, so it isn't offered."
  if (reason === 'noKey') return `${supplierName} has no key on this install.`
  if (reason === 'keyRejected') return `${supplierName} turned down this install's key.`
  return `${supplierName} no longer lists this model for this install.`
}

/** The hosted supplier. Data, not a branch on who the supplier is: it is the
 * hosted choice the installer offers first, and the one supplier whose setup
 * is a connection (an address, and with an account per organization no
 * install key at all) rather than a key alone. */
export const HOSTED_SUPPLIER = 'ottocloud'

/** The supplier a model name with no prefix belongs to. Mirrors the server's
 * `BARE_SPEC_PROVIDER` by hand, like the API types: the server checks a bare
 * name's availability against this supplier, so a reason shown for it must
 * name the same one. */
export const BARE_SPEC_SUPPLIER = 'openai'

/** The supplier the server checks a model against: the name's prefix, or
 * the bare-name supplier when it has none. Read from the name alone, as the
 * server does; a pricing row's own `provider` field does not change it. */
export function supplierOfSpec(spec: string): string {
  return parseNamePattern(spec).provider ?? BARE_SPEC_SUPPLIER
}

/** A supplier's name for the screen: the name the server sent for it, or the
 * id with a capital first letter when the server lists no such supplier (the
 * shipped price list includes makers OttoTester cannot call). */
export function supplierNameFor(
  supplierId: string | undefined,
  suppliers: readonly ModelSupplierStatus[],
): string {
  const known = suppliers.find(s => s.supplier === supplierId)?.name
  if (known) return known
  return supplierId ? supplierId.charAt(0).toUpperCase() + supplierId.slice(1) : 'Its supplier'
}

/** "a" or "an" before a supplier's name. Every supplier name starts with a
 * letter said the way it is written, so the first letter decides. */
function article(name: string): string {
  return /^[aeiou]/i.test(name) ? 'an' : 'a'
}

/** The few words the model catalog shows on a row this install can't use.
 * The table cell's version of `unavailableReasonText`; the full sentence,
 * with where the key goes, is `unavailableReasonHint`. */
export function unavailableReasonLabel(
  reason: ModelUnavailableReason,
  supplierId: string | undefined,
  supplierName: string,
): string {
  switch (reason) {
    case 'noKey':
      return supplierId === HOSTED_SUPPLIER
        ? `Connect to ${supplierName}`
        : `Needs ${article(supplierName)} ${supplierName} key`
    case 'keyRejected':
      return `${supplierName} turned down the key`
    case 'notListed':
      return `No longer offered by ${supplierName}`
    case 'noPrice':
      return 'No price yet'
    case 'unsupported':
      return "Can't be used with OttoTester"
  }
}

/** The full reason behind `unavailableReasonLabel`, for its hover. The
 * sentence Assignments shows, plus the setting to put a key in when one is
 * missing or was turned down. The hosted supplier never gets a key
 * instruction: what it needs is a connection, and with an account per
 * organization the install needs no key, so naming one would point at the
 * wrong fix. */
export function unavailableReasonHint(
  reason: ModelUnavailableReason,
  supplierId: string | undefined,
  supplierName: string,
  keySetting: string | undefined,
): string {
  if (reason === 'noKey' && supplierId === HOSTED_SUPPLIER) return `This install isn't connected to ${supplierName}.`
  if (reason === 'noKey' && keySetting) {
    return `Put ${article(supplierName)} ${supplierName} key in ${keySetting} in your .env file, then restart.`
  }
  if (reason === 'keyRejected' && keySetting) {
    return `${unavailableReasonText(reason, supplierName)} Put a working key in ${keySetting}, then restart.`
  }
  return unavailableReasonText(reason, supplierName)
}

/** Whether the catalog has a real (non-placeholder) price for this
 * model spec. `_unpriced: true` is the discovery-placeholder signal — a
 * row exists but rates aren't filled in, so the cost rollup contributes
 * zero. A missing row counts as unpriced too. An empty pattern (the
 * "inherit / cascade" sentinel) returns `'unknown'` — the resolver will
 * use a lower tier and we can't yet say which model that is; the caller
 * suppresses the badge for that case. */
export function modelPriceStatus(
  namePattern: string,
  pricingRows: readonly ModelPricingRow[],
): 'priced' | 'unpriced' | 'unknown' {
  if (!namePattern.trim()) return 'unknown'
  const row = pricingRows.find((r) => r.namePattern === namePattern)
  if (!row) return 'unpriced'
  return row._unpriced === true ? 'unpriced' : 'priced'
}

/** Whether the catalog says this model can read image input (#1844 Slice 1).
 * Reads the `imageInput` capability flag (from #1842) off the matched
 * pricing row. A missing row, empty pattern, or unset flag → false — we
 * only badge a model as image-capable when the catalog affirmatively says
 * so (fail-safe, matching the backend resolver's default). Purely
 * informational: it drives the "vision enabled" badge and never gates which
 * model an operator may pick. */
export function modelReadsImages(
  namePattern: string,
  pricingRows: readonly ModelPricingRow[],
): boolean {
  if (!namePattern.trim()) return false
  const row = pricingRows.find((r) => r.namePattern === namePattern)
  return row?.imageInput === true
}

/** The matched model's relative cost category (#1844 Slice 2), or undefined
 * (unpriced / unknown). Read off the pricing row's server-derived
 * `costCategory`. Purely informational — the picker shows it as a pill and
 * never gates a choice. */
export function modelCostCategory(
  namePattern: string,
  pricingRows: readonly ModelPricingRow[],
): ModelPricingRow['costCategory'] {
  if (!namePattern.trim()) return undefined
  return pricingRows.find((r) => r.namePattern === namePattern)?.costCategory
}

/** #1844 Slice 1.5 — facet keys the model picker filters on. The editorial
 * keys (`otto-curated`, `frontier`) are the wire contract emitted by the
 * server's `curatedModels.ts` onto `ModelPricingRow.collections`; there is
 * no shared server↔frontend package, so these literals are mirrored by hand
 * (same as `ModelTargetRow`). */
export const FACET_CURATED = 'otto-curated'
export const FACET_FRONTIER = 'frontier'
/** Models the catalog CONFIRMS meet the bar of the agent whose row is being
 * edited: its context size and every capability the bar names. Unlike the
 * editorial facets, membership is agent-dependent, so it cannot be a static
 * `collections` entry: it is computed per render from the served bar plus the
 * row's capability values.
 *
 * The chip asserts EVIDENCE: a model the catalog is silent about is left out of
 * it, while remaining usable under All Models and via search. */
export const FACET_FITS_AGENT = 'fits-agent'

/** One way a model misses an agent's bar. `known: false` means the catalog is
 * silent, so the operator is told we don't know rather than that it fails. */
export interface BarShortfall {
  /** 'context', 'toolCalling', 'reasoning' or 'imageInput', or a capability
   * a newer backend added that this frontend cannot check yet. */
  criterion: string
  known: boolean
}

/** Every way a model misses an agent's bar, in a fixed order (context first,
 * then the bar's capabilities). Empty when it meets the bar, or when there is
 * no bar (an older backend). Pure, so the chip, the warning and the save path
 * all read the same answer.
 *
 * Reasoning counts as met when the model reasons at all; whether its effort can
 * be turned down is deliberately not part of the bar. */
export function barShortfalls(
  row: Pick<ModelPricingRow, 'toolCalling' | 'reasoning' | 'imageInput' | 'maxInputTokens'> | undefined,
  bar: AgentModelBar | undefined,
): BarShortfall[] {
  if (!bar) return []
  const out: BarShortfall[] = []
  // A bar with no context floor (0) does not ask about context at all.
  const context = row?.maxInputTokens
  if (bar.minContextTokens > 0) {
    if (context === undefined || context === null) out.push({ criterion: 'context', known: false })
    else if (context < bar.minContextTokens) out.push({ criterion: 'context', known: true })
  }
  for (const need of bar.needs) {
    if (need !== 'toolCalling' && need !== 'reasoning' && need !== 'imageInput') {
      // A capability this frontend does not know how to check. Reported as
      // unknown so the chip stays evidence-only instead of ignoring it.
      out.push({ criterion: need, known: false })
      continue
    }
    const value = row?.[need]
    if (value === true) continue
    out.push({ criterion: need, known: value === false })
  }
  return out
}

/** The facet keys an option carries, derived from its pricing row: the
 * editorial `collections` (curated / frontier), and `fits-agent` when the
 * row meets the bar of the agent being edited. Pass the already-resolved
 * row — a caller building a whole option list
 * should look each row up once via a map, not re-`find` per option. Returns
 * `[]` for a missing / unpriced row, which leaves the option out of every facet
 * but still visible under "All Models" and search. */
export function optionFacets(
  row: Pick<ModelPricingRow, 'imageInput' | 'collections' | 'toolCalling' | 'reasoning' | 'maxInputTokens'> | undefined,
  /** The bar of the agent being edited, served by the backend and never
   * mirrored here. Omit for a picker with no agent context; the agent facet is
   * then never applied. */
  bar?: AgentModelBar,
): string[] {
  if (!row) return []
  const facets: string[] = row.collections ? [...row.collections] : []
  if (bar && barShortfalls(row, bar).length === 0) facets.push(FACET_FITS_AGENT)
  return facets
}

/** The model-assignable agents, in the order the Assignments page lists them.
 * Shared so the catalog's "Assign to" menu lists them the same way. */
export const ADMIN_AGENT_ORDER: readonly AdminAgentName[] = [
  'planner', 'generator', 'healer', 'analyzer', 'auditor', 'scanner', 'proposer', 'refiner', 'dedupJudge',
]

/** Operator-facing display label for each agent: the Assignments row title, the
 * "Fits the <Agent>" chip, and the catalog's "Assign to" menu, one source so
 * they never disagree. Most are the capitalised name; `dedupJudge` shows as
 * "Deduper", which is why a label can't just capitalise the raw key. */
export const AGENT_LABEL: Record<AdminAgentName, string> = {
  planner: 'Planner', generator: 'Generator', healer: 'Healer',
  analyzer: 'Analyzer', auditor: 'Auditor', scanner: 'Scanner',
  proposer: 'Proposer', refiner: 'Refiner',
  dedupJudge: 'Deduper',
}
